import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";

import paymentMethodApi from "../api/paymentMethodApi";
import supplierApi from "../api/supplierApi";

const pageNo = "PaymentMethodPage";

function PaymentMethodPage() {
  const user = useSelector((state) => state.auth.user);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });

  useEffect(() => {
    async function fetchSuppliers() {
      setLoading(true);
      try {
        const response = await supplierApi.getSupplier();
        if (response.success) {
          setSuppliers(
            response.data.map((supplier) => ({
              value: supplier.EsId,
              label: supplier.EsName,
            })),
          );
          setSupplierId(response.data[0].EsId);
        } else {
          toast.error(
            response.message || "Không thể tải dữ liệu nhà cung cấp.",
          );
        }
      } catch (error) {
        console.error("Lỗi khi gọi API:", error);
        toast.error(error.message, "Đã xảy ra lỗi khi kết nối đến máy chủ.");
      } finally {
        setLoading(false);
      }
    }
    fetchSuppliers();
  }, []);

  const fetchPaymentMethod = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const response = await paymentMethodApi.getBySupplier(supplierId);

      if (response.success) {
        setPaymentMethods(response.data);
      } else {
        toast.error(response.message || "Đã xảy ra lỗi khi tìm kiếm.");
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi tìm kiếm.");
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);
  useEffect(() => {
    fetchPaymentMethod();
  }, [fetchPaymentMethod]);

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setCurrentPaymentMethod(null);
    setFormErrors({});
  };
  const handleEditPaymentMethod = (paymentId) => {
    const selectedPaymentMethod = paymentMethods.find(
      (pm) => pm.paymentId === paymentId,
    );
    setCurrentPaymentMethod(selectedPaymentMethod);
    setOpenAddEditModal(true);
  };
  const handleSave = async () => {
    try {
      const response = await paymentMethodApi.upsert(
        currentPaymentMethod.paymentId,
        currentPaymentMethod.paymentNo,
        currentPaymentMethod.paymentDescription,
        currentPaymentMethod.replacementValue,
        supplierId,
      );
      if (response.success) {
        toast.success("Lưu thành công!");
        handleCloseAddEditModal();
        fetchPaymentMethod();
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi lưu.");
    }
  };

  const handleDeletePaymentMethod = async (paymentId) => {
    setLoading(true);
    try {
      const response = await paymentMethodApi.delete(paymentId); // <-- Sử dụng API thật
      if (response.success) {
        toast.success("Xóa thành công!");
        setConfirmDialogState({
          ...confirmDialogState,
          open: false,
        });
        fetchPaymentMethod();
      } else {
        toast.error(response.message || "Xóa thất bại.");
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi xóa.");
    } finally {
      setLoading(false);
    }
  };

  // --- Định nghĩa các cột cho DataGrid ---
  const columns = [
    {
      field: "paymentNo",
      headerName: "HTTT",
      headerAlign: "center",
      width: 300,
    },
    {
      field: "replacementValue",
      headerName: "Giá trị thay thế",
      headerAlign: "center",
      width: 200,
    },
    {
      field: "paymentDescription",
      headerName: "Mô tả",
      headerAlign: "center",
      width: 500,
    },
    {
      field: "actions",
      headerName: "Chức năng",
      headerAlign: "center",
      type: "actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditPaymentMethod(params.row.paymentId)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => {
              setConfirmDialogState({
                open: true,
                title: "Xoá hình thức thanh toán",
                content: "Bạn có chắc muốn xoá hình thức thanh toán này?",
                onConfirm: () =>
                  handleDeletePaymentMethod(params.row.paymentId),
                onClose: () =>
                  setConfirmDialogState({
                    ...confirmDialogState,
                    open: false,
                  }),
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Khung tìm kiếm */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                select
                label="Nhà cung cấp"
                fullWidth
                value={supplierId || suppliers[0]?.value || ""}
                onChange={(e) => setSupplierId(e.target.value)}
                size="small"
              >
                {suppliers.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setCurrentPaymentMethod(null);
                setOpenAddEditModal(true);
              }}
            >
              Thêm HTTT
            </Button>
          </Grid>
        </Paper>

        <Box sx={{ flexGrow: 1, width: "100%" }}>
          <DataGrid
            rows={paymentMethods}
            columns={columns}
            pageSizeOptions={[50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.paymentId}
            showCellVerticalBorder
            showColumnVerticalBorder
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  Không có hình thức thanh toán nào được tìm thấy.
                </Box>
              ),
            }}
          />
        </Box>

        {/* Modal Thêm/Sửa vai trò */}
        <Dialog open={openAddEditModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentPaymentMethod
              ? "Sửa hình thức thanh toán"
              : "Thêm hình thức thanh toán"}
          </DialogTitle>
          <DialogContent>
            <TextField
              maugin="dense"
              label="Hình thức thanh toán"
              type="text"
              fullWidth
              variant="outlined"
              value={currentPaymentMethod?.paymentNo || ""}
              onChange={(e) =>
                setCurrentPaymentMethod({
                  ...currentPaymentMethod,
                  paymentNo: e.target.value,
                })
              }
              error={!!formErrors.paymentNo}
              helperText={formErrors?.paymentNo || ""}
              sx={{ mt: 1, mb: 1 }}
              size="small"
            />
            <TextField
              maugin="dense"
              label="Giá trị thay thế"
              type="text"
              fullWidth
              variant="outlined"
              value={currentPaymentMethod?.replacementValue || ""}
              onChange={(e) =>
                setCurrentPaymentMethod({
                  ...currentPaymentMethod,
                  replacementValue: e.target.value,
                })
              }
              error={!!formErrors.replacementValue}
              helperText={formErrors?.replacementValue || ""}
              sx={{ mt: 1, mb: 1 }}
              size="small"
            />
            <TextField
              label="Mô tả"
              type="text"
              fullWidth
              variant="outlined"
              value={currentPaymentMethod?.paymentDescription || ""}
              onChange={(e) =>
                setCurrentPaymentMethod({
                  ...currentPaymentMethod,
                  paymentDescription: e.target.value,
                })
              }
              error={!!formErrors.paymentDescription}
              helperText={formErrors.paymentDescription}
              sx={{ mb: 1 }}
              size="small"
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={confirmDialogState.open}
          title={confirmDialogState.title}
          content={confirmDialogState.content}
          onConfirm={confirmDialogState.onConfirm}
          onClose={confirmDialogState.onClose}
        />
      </Box>
    </LocalizationProvider>
  );
}

export default PaymentMethodPage;
